import React, { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Modal, useToast } from '../ui/index.jsx'
import { useLanguage } from '../../i18n/index.jsx'
import api from '../../utils/api.js'
import { validatePassword } from '../../utils/helpers.js'
import useAuthStore from '../../store/authStore.js'
import CalendarFeedSettings from '../settings/CalendarFeedSettings.jsx'
import AvatarUploader from './AvatarUploader.jsx'
import ProfileEditor from './ProfileEditor.jsx'
import DensityToggle from '../ui/DensityToggle'
import MfaSetup from './MfaSetup.jsx'
import { logError } from '../../utils/logError.js'
import PasswordChanger from './PasswordChanger.jsx'
import { APP_ROUTES } from '../../config/appRoutes.js'
import type { User, Department, UserUiPreferences } from '../../types/domain.js'

interface UserProfileModalProps {
  open: boolean
  onClose: () => void
  user: User | null | undefined
}

interface ProfileState {
  email: string
  work_title: string
  phone: string
  department_id: string | number | null
}

type MfaStep = 'idle' | 'setup' | 'recovery' | 'disable'

interface PasswordFormState {
  current_password: string
  new_password: string
  confirm_password: string
}

export default function UserProfileModal({ open, onClose, user }: UserProfileModalProps) {
  const { show } = useToast()
  const { updateUser, logout } = useAuthStore()
  const { t } = useLanguage()

  // ── Contact/profile info state ──────────────────────────────
  const [profile, setProfile] = useState<ProfileState>({
    email:      '',
    work_title: '',
    phone:      '',
    department_id: '',
  })
  const [departments, setDepartments] = useState<Department[]>([])
  const [profileLoading, setProfileLoading] = useState<boolean>(false)
  const [profileSuccess, setProfileSuccess] = useState<boolean>(false)

  // Populate from user prop whenever modal opens
  useEffect(() => {
    if (open && user) {
      setProfile({
        email:      user.email      || '',
        work_title: user.work_title || '',
        phone:      user.phone      || '',
        department_id: user.department_id ?? '',
      })
      setProfileSuccess(false)
      api.get('/departments').then(({ data }) => setDepartments(data as Department[])).catch(err => logError('userProfile.loadDepartments', err))
    }
  }, [open, user])

  // ── MFA state ─────────────────────────────────────────────────
  const [mfaEnabled,      setMfaEnabled]      = useState<boolean>(false)
  const [mfaLoading,      setMfaLoading]      = useState<boolean>(false)
  const [mfaStep,         setMfaStep]         = useState<MfaStep>('idle')
  const [mfaQr,           setMfaQr]           = useState<string | null>(null)
  const [mfaSecret,       setMfaSecret]       = useState<string>('')
  const [mfaCode,         setMfaCode]         = useState<string>('')
  const [mfaRecovery,     setMfaRecovery]     = useState<string[]>([])
  const [mfaError,        setMfaError]        = useState<string>('')
  const [mfaDisablePw,    setMfaDisablePw]    = useState<string>('')

  // Fetch MFA status when modal opens
  useEffect(() => {
    if (open && user) {
      api.get('/auth/mfa/status').then(({ data }) => setMfaEnabled(!!(data as Record<string, unknown>).mfa_enabled)).catch(err => logError('userProfile.loadMfaStatus', err))
      setMfaStep('idle')
      setMfaQr(null)
      setMfaSecret('')
      setMfaCode('')
      setMfaRecovery([])
      setMfaError('')
      setMfaDisablePw('')
    }
  }, [open, user])

  const handleMfaSetup = async () => {
    setMfaLoading(true); setMfaError('')
    try {
      const { data } = await api.post('/auth/mfa/setup') as { data: Record<string, string> }
      setMfaQr(data.qr_code)
      setMfaSecret(data.secret)
      setMfaStep('setup')
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } }
      setMfaError(e.response?.data?.error || t('somethingWentWrong'))
    } finally {
      setMfaLoading(false)
    }
  }

  const handleMfaVerifySetup = async (e: React.FormEvent) => {
    e.preventDefault()
    setMfaLoading(true); setMfaError('')
    try {
      const { data } = await api.post('/auth/mfa/verify-setup', { token: mfaCode }) as { data: { recovery_codes: string[] } }
      setMfaRecovery(data.recovery_codes)
      setMfaEnabled(true)
      setMfaStep('recovery')
      show(t('mfaEnabled'), 'success')
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } }
      setMfaError(e.response?.data?.error || t('invalidCode'))
    } finally {
      setMfaLoading(false)
    }
  }

  const handleMfaDisable = async (e: React.FormEvent) => {
    e.preventDefault()
    setMfaLoading(true); setMfaError('')
    try {
      await api.post('/auth/mfa/disable', { password: mfaDisablePw })
      setMfaEnabled(false)
      setMfaStep('idle')
      setMfaDisablePw('')
      show(t('mfaDisabled'), 'success')
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } }
      setMfaError(e.response?.data?.error || t('somethingWentWrong'))
    } finally {
      setMfaLoading(false)
    }
  }

  // ── Default landing page state ──────────────────────────────
  const [defaultPage, setDefaultPage] = useState<string>('')
  const [defaultPageSaving, setDefaultPageSaving] = useState<boolean>(false)

  useEffect(() => {
    if (open && user) {
      setDefaultPage(user.ui_preferences?.preferred_landing || '')
    }
  }, [open, user])

  const handleDefaultPageChange = async (value: string) => {
    setDefaultPage(value)
    setDefaultPageSaving(true)
    try {
      const { data } = await api.put('/users/me/ui-preferences', { preferred_landing: value || null })
      if (user) updateUser({ ...user, ui_preferences: data as UserUiPreferences })
    } catch {
      show(t('saveFailed'), 'error')
    } finally {
      setDefaultPageSaving(false)
    }
  }

  // ── File open behavior preference ───────────────────────────
  const [fileOpenMode, setFileOpenMode] = useState<string>('ask')
  const [fileOpenSaving, setFileOpenSaving] = useState<boolean>(false)

  useEffect(() => {
    if (open && user) {
      const raw = user.ui_preferences?.file_open_mode
      // Legacy 'popup' value (window.open-based mode) maps to the new 'preview' option.
      setFileOpenMode(raw === 'popup' ? 'preview' : (raw || 'ask'))
    }
  }, [open, user])

  const handleFileOpenModeChange = async (value: string) => {
    setFileOpenMode(value)
    setFileOpenSaving(true)
    try {
      const { data } = await api.put('/users/me/ui-preferences', { file_open_mode: value })
      if (user) updateUser({ ...user, ui_preferences: data as UserUiPreferences })
    } catch {
      show(t('saveFailed'), 'error')
    } finally {
      setFileOpenSaving(false)
    }
  }

  // ── GDPR self-service state ───────────────────────────────────
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false)
  const [deleteOpen,      setDeleteOpen]      = useState<boolean>(false)
  const [deletePw,        setDeletePw]        = useState<string>('')
  const [deleteConfirm,   setDeleteConfirm]   = useState<string>('')
  const [deleteLoading,   setDeleteLoading]   = useState<boolean>(false)
  const [deleteError,     setDeleteError]     = useState<string>('')

  // ── Password change state ────────────────────────────────────
  const [form, setForm] = useState<PasswordFormState>({
    current_password: '',
    new_password: '',
    confirm_password: '',
  })
  const [error,          setError]          = useState<string>('')
  const [loading,        setLoading]        = useState<boolean>(false)
  const [success,        setSuccess]        = useState<boolean>(false)
  const profileTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const passwordTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => {
    if (profileTimerRef.current) clearTimeout(profileTimerRef.current)
    if (passwordTimerRef.current) clearTimeout(passwordTimerRef.current)
  }, [])

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setProfileLoading(true)
    setProfileSuccess(false)
    try {
      const res = await api.put('/users/me/profile', {
        ...profile,
        department_id: profile.department_id ? parseInt(String(profile.department_id)) : null,
      })
      if (user) updateUser({ ...user, ...(res.data as Partial<User>) })
      setProfileSuccess(true)
      profileTimerRef.current = setTimeout(() => setProfileSuccess(false), 3000)
    } catch {
      show(t('saveFailed'), 'error')
    } finally {
      setProfileLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess(false)

    if (!form.current_password) {
      setError(t('currentPasswordRequired'))
      return
    }
    if (!form.new_password) {
      setError(t('newPasswordRequired'))
      return
    }
    const pwErrors = validatePassword(form.new_password)
    if (pwErrors.length) {
      setError(pwErrors.map(k => t(k) || k).join(', '))
      return
    }
    if (form.new_password !== form.confirm_password) {
      setError(t('passwordsDoNotMatch'))
      return
    }

    setLoading(true)
    try {
      await api.post('/users/me/password', {
        current_password: form.current_password,
        new_password: form.new_password,
      })
      setSuccess(true)
      setForm({ current_password: '', new_password: '', confirm_password: '' })
      passwordTimerRef.current = setTimeout(() => {
        setSuccess(false)
        onClose()
      }, 2000)
    } catch (err) {
      const e = err as { response?: { data?: { error_code?: string; error?: string } } }
      const code = e.response?.data?.error_code
      const msg  = code === 'PASSWORD_BREACHED'
        ? t('passwordBreached')
        : (e.response?.data?.error || t('failedToChangePassword'))
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadData = async () => {
    setDownloadLoading(true)
    try {
      const { data } = await api.get('/users/me/data-export')
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `brighella-data-${new Date().toISOString().slice(0, 10)}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      show(t('somethingWentWrong'), 'error')
    } finally {
      setDownloadLoading(false)
    }
  }

  const handleDeleteAccount = async (e: React.FormEvent) => {
    e.preventDefault()
    if (deleteConfirm !== 'DELETE') return setDeleteError(t('typeDeleteToConfirm'))
    if (!deletePw) return setDeleteError(t('currentPasswordRequired'))
    setDeleteLoading(true); setDeleteError('')
    try {
      await api.delete('/users/me', { data: { password: deletePw, confirm: 'DELETE' } })
      onClose()
      logout()
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } }
      setDeleteError(e.response?.data?.error || t('somethingWentWrong'))
    } finally {
      setDeleteLoading(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={t('profileInfo')} size="sm">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

        <AvatarUploader
          user={user ?? null}
          onUserUpdate={updateUser}
          onSaveProfile={() => { void handleProfileSave({ preventDefault: () => undefined } as React.FormEvent) }}
          profileLoading={profileLoading}
          profileSuccess={profileSuccess}
        />

        <ProfileEditor
          profile={profile}
          setProfile={(p) => setProfile(p)}
          departments={departments}
          profileLoading={profileLoading}
          profileSuccess={profileSuccess}
          onSave={handleProfileSave}
        />

        {/* Brighella v2 (ADR-013 #6) — Density toggle. Persists per-user via
            useCohesionStore + applies app-wide through [data-density]. */}
        <div>
          <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
            {t('displayDensity')}
          </div>
          <DensityToggle />
        </div>

        <MfaSetup
          mfaEnabled={mfaEnabled}
          mfaLoading={mfaLoading}
          mfaStep={mfaStep}
          mfaQr={mfaQr}
          mfaSecret={mfaSecret}
          mfaCode={mfaCode}
          setMfaCode={setMfaCode}
          mfaRecovery={mfaRecovery}
          mfaError={mfaError}
          mfaDisablePw={mfaDisablePw}
          setMfaDisablePw={setMfaDisablePw}
          onSetup={handleMfaSetup}
          onVerifySetup={handleMfaVerifySetup}
          onDisable={handleMfaDisable}
          onSetStep={setMfaStep}
          onClearError={() => setMfaError('')}
        />

        <CalendarFeedSettings />

        <div>
          <Link
            to="/me/id-card"
            onClick={onClose}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
              padding: '0.375rem 0.75rem', borderRadius: 'var(--radius)',
              border: '1px solid var(--border-default)', background: 'var(--bg-elevated)',
              color: 'var(--text-secondary)', fontSize: 'var(--text-base)', textDecoration: 'none',
              transition: 'border-color 0.15s, color 0.15s',
            }}
          >
            🪪 {t('idCardTitle')}
          </Link>
        </div>

        <div>
          <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{t('landingPage')}</div>
          <div style={{ fontSize: 'var(--text-caption)', color: 'var(--color-text-muted)', marginBottom: '0.5rem' }}>{t('landingPageDesc')}</div>
          <select
            value={defaultPage}
            onChange={e => handleDefaultPageChange(e.target.value)}
            disabled={defaultPageSaving}
            style={{ width: '100%', padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)', fontSize: 'var(--text-base)' }}
          >
            <option value="">{t('default')}</option>
            {APP_ROUTES.map(r => (
              <option key={r.key} value={r.key}>{t(r.label)}</option>
            ))}
          </select>
        </div>

        <div>
          <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{t('fileOpenSettingsLabel')}</div>
          <div style={{ fontSize: 'var(--text-caption)', color: 'var(--color-text-muted)', marginBottom: '0.5rem' }}>{t('fileOpenSettingsDesc')}</div>
          <select
            value={fileOpenMode}
            onChange={e => handleFileOpenModeChange(e.target.value)}
            disabled={fileOpenSaving}
            style={{ width: '100%', padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)', fontSize: 'var(--text-base)' }}
          >
            <option value="ask">{t('fileOpenSettingsAsk')}</option>
            <option value="preview">{t('fileOpenSettingsPopup')}</option>
            <option value="tab">{t('fileOpenSettingsTab')}</option>
          </select>
        </div>

        <PasswordChanger
          form={form}
          setForm={setForm}
          error={error}
          success={success}
          loading={loading}
          onSubmit={handleSubmit}
          onClose={onClose}
        />

        {/* My Data — GDPR Art. 20 */}
        <div>
          <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{t('myDataSection')}</div>
          <div style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>{t('downloadMyDataDesc')}</div>
          <button onClick={handleDownloadData} disabled={downloadLoading} className="btn btn-secondary" style={{ fontSize: 'var(--text-base)' }}>
            {downloadLoading ? t('preparingDownload') : t('downloadMyData')}
          </button>
        </div>

        {/* Danger Zone — GDPR Art. 17 */}
        <div style={{ borderTop: '1px solid var(--border-default)', paddingTop: '1rem' }}>
          <div style={{ fontWeight: 600, marginBottom: '0.25rem', color: 'var(--danger)' }}>{t('deleteMyAccount')}</div>
          <div style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>{t('deleteAccountWarning')}</div>
          {!deleteOpen ? (
            <button
              onClick={() => setDeleteOpen(true)}
              style={{ fontSize: 'var(--text-base)', padding: '0.375rem 0.75rem', background: 'transparent', border: '1px solid var(--danger)', color: 'var(--danger)', borderRadius: 'var(--radius)', cursor: 'pointer' }}
            >
              {t('deleteMyAccount')}
            </button>
          ) : (
            <form onSubmit={handleDeleteAccount} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {deleteError && (
                <div style={{ fontSize: 'var(--text-caption)', color: 'var(--danger)', padding: '0.375rem 0.5rem', background: 'var(--danger-dim)', borderRadius: 'var(--radius-sm)' }}>
                  {deleteError}
                </div>
              )}
              <input id="delete-account-password" type="password" value={deletePw} onChange={e => setDeletePw(e.target.value)}
                placeholder={t('passwordLabel')} aria-label={t('passwordLabel')} className="field-input" style={{ fontSize: 'var(--text-base)' }} />
              <input id="delete-account-confirm" type="text" value={deleteConfirm} onChange={e => setDeleteConfirm(e.target.value)}
                placeholder={t('typeDeleteToConfirm')} aria-label={t('typeDeleteToConfirm')} className="field-input" autoComplete="off"
                style={{ fontSize: 'var(--text-base)', fontFamily: 'var(--font-mono)', letterSpacing: '0.05em' }} />
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button type="submit" disabled={deleteLoading || deleteConfirm !== 'DELETE'}
                  style={{ padding: '0.375rem 0.75rem', background: 'var(--danger)', color: 'var(--text-primary)', border: 'none', borderRadius: 'var(--radius)', cursor: 'pointer', fontSize: 'var(--text-base)', fontWeight: 500, opacity: deleteConfirm !== 'DELETE' ? 0.5 : 1 }}>
                  {deleteLoading ? t('confirmingDeletion') : t('deleteAccountConfirm')}
                </button>
                <button type="button" onClick={() => { setDeleteOpen(false); setDeletePw(''); setDeleteConfirm(''); setDeleteError('') }}
                  style={{ padding: '0.375rem 0.75rem', background: 'transparent', border: '1px solid var(--border-default)', color: 'var(--text-muted)', borderRadius: 'var(--radius)', cursor: 'pointer', fontSize: 'var(--text-base)' }}>
                  {t('cancel')}
                </button>
              </div>
            </form>
          )}
        </div>

      </div>
    </Modal>
  )
}
